// ============================================================================
// apps/web/src/lib/nav-modules.ts
// ----------------------------------------------------------------------------
// SOURCE DE VÉRITÉ UNIQUE des modules de Magic.
// Alimente à la fois :
//   - la sidebar / le tiroir mobile (AppShell) ;
//   - les tuiles de la page d'accueil (DashboardPage).
// Objectif : aucun module ne peut plus manquer d'un côté ou de l'autre. Pour
// ajouter/retirer un module, il suffit de modifier CE fichier.
//
// Ordre = regroupement par domaine (reflété tel quel dans la sidebar) :
//   1. Pilotage         : Accueil, Supervision, Saisie rapide
//   2. Exploitation jour : Menu, Planning, Retours, To-Do, Hygiène, Production
//   3. Caisse & finances : Caisses, Reporting CA, Reporting caisse, Import,
//                          Clyo→Pennylane, Achats
//   4. Commercial & équipe : Newsletter, Événementiel, Tenues, Candidatures
//   5. Référentiels     : Produits, Étiquettes
//   6. Conformité & savoir : Légal, Procédures, Formation
//   7. Système          : Sauvegardes, Réglages, Mon profil, Mode d'emploi
//
// Champs :
//   - inSidebar (déf. true)  : présent dans la barre de navigation.
//   - inHome    (déf. true)  : présent comme TUILE sur l'accueil. Mis à false
//     pour les entrées rendues différemment sur l'accueil (Accueil lui-même,
//     « Saisie rapide » = bouton d'action, « Mon profil » = bannière).
// ============================================================================
import {
  ArrowLeftRight, BarChart3, Book, BookText, Boxes, Calendar, ChefHat, ClipboardList, CookingPot, DatabaseBackup, Gauge, GraduationCap,
  Home, ListChecks, Mail, PartyPopper, Receipt, Settings, ShieldCheck, Shirt, Sparkles, Tag,
  TrendingUp, Upload, User, Users, UtensilsCrossed, Wallet, Zap,
} from 'lucide-react';
import { Capability } from '@magic/shared';

// Type d'icône lucide (aligné sur le style existant du repo).
type IconType = typeof Home;

export type ModuleDef = {
  key: string;
  label: string;            // libellé sidebar
  title?: string;           // titre de la tuile accueil (défaut = label)
  description: string;      // description de la tuile accueil
  icon: IconType;
  to?: string;              // route interne
  href?: string;            // lien externe (nouvel onglet)
  capability?: string;      // capacité requise (sinon visible par tous)
  inSidebar?: boolean;      // défaut true
  inHome?: boolean;         // défaut true (rendu comme tuile)
};

// Lien STAFF vers la composition du menu. menu.lepitchoun-traiteur.fr (nu) sert
// la carte publique aux clients ; ?admin ouvre le login + composer.
export const MENU_URL = 'https://menu.lepitchoun-traiteur.fr/?admin';
// Carte PUBLIQUE consultable par les clients (menu nu, sans ?admin).
export const MENU_PUBLIC_URL = 'https://menu.lepitchoun-traiteur.fr';
export const TODO_URL = 'https://todo.lepitchoun-traiteur.fr';

export const MODULES: ModuleDef[] = [
  // ── 1. Pilotage ───────────────────────────────────────────────────────────
  { key: 'home', label: 'Accueil', icon: Home, to: '/', description: '', inHome: false },
  {
    key: 'supervision', label: 'Supervision', icon: Gauge, to: '/supervision',
    capability: Capability.SUPERVISION_READ,
    description: 'Tableau de bord multi-PV : hygiène, caisse, retours & demandes SAV. Accès réservé.',
  },
  {
    key: 'saisie', label: 'Saisie rapide', icon: Zap, to: '/feedback/new',
    capability: Capability.FEEDBACK_WRITE, description: '', inHome: false,
  },

  // ── 2. Exploitation quotidienne ────────────────────────────────────────────
  {
    key: 'menu', label: 'Menu du mois', icon: ChefHat, href: MENU_URL,
    description: 'Catalogue de plats, composer mensuel, exports PDF/email/Insta',
  },
  {
    key: 'menu-public', label: 'Menu (clients)', icon: ChefHat, href: MENU_PUBLIC_URL,
    description: 'La carte du mois telle que la voient les clients (menu public, sans connexion).',
    inHome: false,
  },
  {
    key: 'planning', label: 'Planning', icon: Calendar, to: '/planning',
    capability: Capability.PLANNING_READ_OWN_LOCATIONS,
    description: '5 vues : par staff, par poste, mensuel PV, planning type cyclique, génération auto',
  },
  {
    key: 'feedback', label: 'Retours produits', icon: Sparkles, to: '/feedback',
    capability: Capability.FEEDBACK_READ,
    description: 'Liste de tous les retours terrain + filtres',
  },
  {
    key: 'todo', label: 'To-Do', icon: ListChecks, href: TODO_URL,
    description: 'Tâches & demandes (MM Distribution & autres entités) — app dédiée',
  },
  {
    key: 'hygiene', label: 'Hygiène et matériel', icon: ShieldCheck, to: '/hygiene',
    capability: Capability.HYGIENE_READ,
    description: 'Alertes (péremption, température) + tâches récurrentes par PV',
  },
  {
    key: 'production', label: 'Production', icon: CookingPot, to: '/production',
    capability: Capability.CUISINE_READ,
    description: 'Fiches techniques cuisine, production journalière (bacs GN), coût matière & répartition par magasin. Accès réservé.',
  },

  // ── 3. Caisse & finances ────────────────────────────────────────────────────
  {
    key: 'cash', label: 'Caisses', icon: Wallet, to: '/cash',
    capability: Capability.CASH_READ,
    description: 'Comptage de caisse quotidien par PV + écarts',
  },
  {
    key: 'reporting', label: 'Reporting CA', icon: TrendingUp, to: '/reporting',
    capability: Capability.REPORTING_READ,
    description: 'Tendances du CA HT par PV — comparaison pluriannuelle (tableau + graphique). Accès réservé.',
  },
  {
    key: 'caisse', label: 'Reporting caisse', icon: BarChart3, to: '/caisse',
    capability: Capability.CAISSE_READ,
    description: 'Analyse des ventes par PV depuis les archives Clyo (CA, tickets, heures, canal, top/pires, livraison, anti-vol, comparaisons). Accès réservé.',
    inHome: true,
  },
  {
    key: 'caisse-import', label: 'Import caisse', icon: Upload, to: '/caisse/import',
    capability: Capability.CAISSE_MANAGE,
    description: 'Importer les archives fiscales Clyo (dossier déposé tel quel, PV auto-détecté).',
    inSidebar: true, inHome: false,
  },
  {
    key: 'clyo-convert', label: 'Clyo → Pennylane', icon: ArrowLeftRight, to: '/reporting/convertisseur',
    capability: Capability.REPORTING_READ,
    description: 'Convertit un export CSV de la caisse Clyo au format d\'import comptable Pennylane (100 % navigateur). Distinct des archives fiscales NF525.',
  },
  {
    key: 'achats', label: 'Achats', icon: Receipt, to: '/achats',
    capability: Capability.ACHATS_READ,
    description: 'Factures fournisseurs Pennylane : lecture auto (gabarits), mercuriale & écarts de prix. Accès réservé.',
  },

  // ── 4. Commercial & équipe ───────────────────────────────────────────────────
  {
    key: 'newsletter', label: 'Newsletter', icon: Mail, to: '/newsletter',
    capability: Capability.NEWSLETTER_MANAGE,
    description: 'Collecte d\'emails clients (QR/PV), file d\'ajout Brevo, QR & affiche A4. Accès réservé.',
  },
  {
    key: 'event', label: 'Événementiel', icon: PartyPopper, to: '/event',
    capability: Capability.EVENT_ACCESS,
    description: 'Prestataires event (carnet exportable pour les clients) + accès au suivi commercial. Accès réservé.',
  },
  {
    key: 'portage', label: 'Portage repas', icon: UtensilsCrossed, to: '/portage',
    capability: Capability.ADMIN_ALL,
    description: 'Demandes de portage de repas à domicile (formulaire public) + suivi et réglages. Accès réservé.',
  },
  {
    key: 'wardrobe', label: 'Tenues', icon: Shirt, to: '/wardrobe',
    capability: Capability.WARDROBE_READ,
    description: 'Garde-robe (événementiel & magasin) + suivi des prêts aux extras/équipe. Accès réservé.',
  },
  {
    key: 'candidatures', label: 'Candidatures', icon: Users, to: '/candidatures',
    capability: Capability.CANDIDATURES_MANAGE,
    description: 'CVthèque — dépôt et suivi des candidatures par poste (vente, cuisine, extra, livreur, maître d\'hôtel). Accès réservé.',
  },

  // ── 5. Référentiels ──────────────────────────────────────────────────────────
  {
    key: 'produits', label: 'Produits', icon: Boxes, to: '/produits',
    capability: Capability.ADMIN_ALL,
    description: 'Référentiel central des produits vendus + fiches éditables (socle des étiquettes). Accès réservé.',
  },
  {
    key: 'etiquettes', label: 'Étiquettes', icon: Tag, to: '/etiquettes',
    capability: Capability.ADMIN_ALL,
    description: 'Étiquettes de prix magasin (gabarits Pitchoun/NPA, planche imprimable). Accès réservé.',
  },

  // ── 6. Conformité & savoir ───────────────────────────────────────────────────
  {
    key: 'legal', label: 'Documents légaux', icon: ClipboardList, to: '/legal',
    capability: Capability.LEGAL_READ,
    description: 'Registre central + alerte si expiration < 90 jours',
  },
  {
    key: 'procedures', label: 'Procédures', icon: BookText, to: '/procedures',
    description: 'Procédures d\'entreprise à consulter (magasins & équipe)',
  },
  {
    key: 'formation', label: 'Formation', icon: GraduationCap, to: '/formation',
    description: 'Quizz & formations de l\'équipe (vendeur, hygiène…) + suivi des acquis',
  },

  // ── 7. Système ────────────────────────────────────────────────────────────────
  {
    key: 'sauvegardes', label: 'Sauvegardes', icon: DatabaseBackup, to: '/sauvegardes',
    capability: Capability.REPORTING_READ,
    description: 'État des sauvegardes automatiques (Mac) + alerte si retard ou échec. Accès réservé.',
  },
  {
    key: 'settings', label: 'Réglages', icon: Settings, to: '/settings',
    capability: Capability.USERS_READ_ALL,
    description: 'Utilisateurs, permissions, points de vente, postes, mode d\'emploi',
  },
  {
    key: 'profile', label: 'Mon profil', icon: User, to: '/me/profile',
    description: '', inHome: false,
  },
  {
    key: 'help', label: 'Mode d\'emploi', icon: Book, to: '/help',
    description: 'Guide complet de la plateforme (tous les modules).',
  },
];

// Sous-ensembles pratiques pour chaque surface.
export const SIDEBAR_MODULES = MODULES.filter((m) => m.inSidebar !== false);
export const HOME_TILES = MODULES.filter((m) => m.inHome !== false);
